/**
 * Calculator1 JS controller. (Maatilatalouden työtulo- ja vakuutusmaksulaskuri)
 *
 * This class handles processing of first calculator forms.
 */

// Use jQuery as $ within this file scope.
const $ = jQuery;

/**
 * Class Calculator1. This gets mapped with the keyword Calculator1 and its
 * docready method is fired automatically on pages where there's a
 * class called 'Calculator1' on the html tag.
 */
class Calculator1 {

    /**
     * The class constructor is for binding class properties.
     */
    constructor() {
    }

    /**
     * Cache dom elements for use in the methods
     */
    cache() {

        // Instance of the Calculators utility class.
        this.utils = Theme.getController( 'Calculators' );

        // Store a shorthand reference to translatable strings.
        this.strings = themeData.themeStrings.calculators;

        // Calculator form.
        this.$form = this.utils.initForm( 'maatilatalouden-tyotulo-ja-vakuutusmaksulaskuri' );

    }

    /**
     * All dom events of the class.
     * @return {void}
     */
    events() {

        // Bind a handler to form submit.
        this.$form.on( 'submit', e => this.calculateForm( e ) );

        // Prevent native HTML validation
        this.$form[0].noValidate = true;

        // Auto-submit the form? See Calculators class for details.
        if ( this.$form.data( 'submit' ) ) {
            this.$form.trigger( 'submit' );
        }

    }

    /**
     * Processing function for the form.
     *
     * @param {object} e Event object.
     */
    calculateForm( e ) {

        // Stop propagation
        Theme.Common.stop( e );

        // Store reference to current object for later use.
        const self = this;

        // Placeholder for calculated data.
        this.calculatedData = {
            'rows': {
                'field_income': null,
                'field_income_min_max': null,
                'forest_income': null,
                'ancillary_work_income': null,
                'total_income': null,
                'mela_insurance_cost': null
            },
            'insurance_required': false
        };

        // Validate and store form values in class property validatedData.
        this.validatedData = this.utils.validateForm( this.$form, {
            'enterpreneurs': 1,        // int 1-2 (pers)
            'area_of_field': 0,        // float >=0 (ha)
            'area_of_forest': 0,       // float >=0 (ha)
            'location_of_forest': 1,   // int 1-3 (1 = Etelä-Suomi, 2 = Keski-Suomi, 3 = Pohjois-Suomi)
            'ancillary_work_income': 0 // float >=0 (EUR/year)
        });

        // Set ancilliary work income.
        if ( this.validatedData.ancillary_work_income ) {
            this.calculatedData.rows['ancillary_work_income'] = this.validatedData.ancillary_work_income;
        }

        // Calculate income from field area, but only enter this part if field area has a valid value.
        if ( this.validatedData.area_of_field > 0 ) {

            // Calculate total income for field area.
            this.calculatedData.rows['field_income'] = this.utils.calculateAreaIncome(
                this.validatedData.area_of_field,
                1
            );

            // Calculate field income min and max values, but only if there actually was income from field.
            if ( this.calculatedData.rows.field_income ) {

                let fieldIncomeMin = ( this.validatedData.enterpreneurs === 1 ? 0.70 : 0.85 ) * this.calculatedData.rows['field_income'];
                let fieldIncomeMax = ( this.validatedData.enterpreneurs === 1 ? 1.15 : 1.30 ) * this.calculatedData.rows['field_income'];

                // Round field income min/max values to full euros.
                fieldIncomeMin = this.utils.round( fieldIncomeMin );
                fieldIncomeMax = this.utils.round( fieldIncomeMax );

                // Store field income min and max values.
                this.calculatedData.rows['field_income_min_max'] = fieldIncomeMin + ' - ' + fieldIncomeMax;

            }

            // Round field area income to nearest full EUR.
            this.calculatedData.rows['field_income'] = this.utils.round(
                this.calculatedData.rows['field_income']
            );

        }

        // Calculate income from forest area, but only enter this part if forest area has a valid value.
        if ( this.validatedData.area_of_forest > 0 ) {
            this.calculatedData.rows['forest_income'] = this.utils.calculateAreaIncome(
                this.validatedData.area_of_forest,
                2,
                this.validatedData.location_of_forest
            );
            this.calculatedData.rows['forest_income'] = this.utils.round(
                this.calculatedData.rows['forest_income']
            );
        }

        // Calculate total income (field + forest + ancilliary work).
        this.calculatedData.rows['total_income'] = 0;
        [ 'field_income', 'forest_income', 'ancillary_work_income' ].map( function( item ) {
            if ( self.calculatedData.rows[item]) {
                self.calculatedData.rows['total_income'] += self.calculatedData.rows[item];
            }
        });

        // Calculate MELA insurance cost. This is a composite value comprised of the business insurance costs (MYEL insurance
        // and MATA insurance) divided by the number of enterpreneurs, and the group life insurance cost.
        this.calculatedData.rows['mela_insurance_cost'] = 0;

        // Calculate MYELha. When calculating MYELha, field counts directly towards total MYEL ha, but forest is
        // counted using a factor depending on location: Etelä-Suomi 1/10, Keski-Suomi 1/15, Pohjois-Suomi 1/20.
        let MYELha = this.validatedData.area_of_field ? this.validatedData.area_of_field : 0;
        if ( this.validatedData.area_of_forest ) {
            const k = this.validatedData.location_of_forest == 2 ? 1 / 15 : ( this.validatedData.location_of_forest == 3 ? 1 / 20 : 1 / 10 );
            MYELha += k * this.validatedData.area_of_forest;
        }

        // Check if MELA insurance is required. This depends on how many MYEL ha of field and/or forest there are
        // and whether the total income divided by number of enterpreneurs is over the prespecified limit value.
        this.calculatedData['insurance_required'] = MYELha >= 5 && ( this.calculatedData.rows['total_income'] / this.validatedData.enterpreneurs ) >= this.utils.insuranceRequiredIncome;

        // Calculate income for insurance costs, depending on the number of enterpreneurs.
        const insuranceIncome = this.calculatedData.rows['total_income'] / this.validatedData.enterpreneurs;

        // Calculate MYEL insurance cost. Note: calculateMYELInsurance() returns an array, where the first
        // index is the MYEL insurance cost, so we grab that directly.
        const MYELInsuranceCost = this.utils.calculateMYELInsurance(
            insuranceIncome,
            undefined,
            undefined,
            undefined,
            0
        )[0];

        // Calculate MATA insurance cost. Params: total income, k (is insurance is required?) and a (1).
        const MATAInsuranceCost = this.utils.calculateMATAInsurance(
            insuranceIncome,
            this.calculatedData['insurance_required'],
            1,
            undefined,
            0
        )[0];

        // Calculate total insurance costs.
        this.calculatedData.rows['mela_insurance_cost'] = ( MYELInsuranceCost + MATAInsuranceCost ) + this.utils.groupLifeInsuranceCostEnterpreneurs;
        this.calculatedData.rows['mela_insurance_cost'] = this.utils.round(
            this.calculatedData.rows['mela_insurance_cost']
        );

        // Render output.
        this.outputForm();

    }

    /**
     * Render output for the first form.
     */
    outputForm() {

        // Stop here if errors were found.
        if ( this.utils.validator.errorLog.length ) {
            this.utils.outputErrors( this.$form );
            return false;
        }

        // Create container for output.
        const $container = this.utils.renderResultsContainer( this.$form );

        // Add calculation parameters.
        const $params = this.utils.renderParams(
            this.$form,
            this.validatedData,
            {
                'area_of_field': {
                    'ext': 'ha'
                },
                'area_of_forest': {
                    'ext': 'ha'
                },
                'location_of_forest': {
                    'skip': ! this.validatedData.area_of_forest || this.validatedData.area_of_forest <= 0
                }
            }
        );
        $params.appendTo( $container );

        // Create and insert results after the form.
        const $table = this.utils.renderData( this.calculatedData.rows, 0 );
        $table.appendTo( $container );

        // Display info texts.
        const $info = this.utils.renderInfo([
            this.calculatedData.insurance_required ? this.strings.insurance_info_required : this.strings.insurance_info_optional,
            this.validatedData.enterpreneurs == 2 ? this.strings.insurance_info_split_cost : null,
            this.strings.insurance_info_age_group
        ]);
        $info.appendTo( $container );

        // Append action buttons.
        const $buttons = this.utils.renderButtons( this.$form );
        $buttons.appendTo( $container );

        // Add extra content.
        this.utils.renderExtra( this.$form, $container );

    }

    /**
     * Run when the document is ready.
     */
    docReady() {
        this.cache();
        if ( this.$form ) {
            this.events();
        }
    }

}

// Export the class reference.
module.exports = Calculator1;



// WEBPACK FOOTER //
// ./assets/scripts/calculator1.js